// [PURPOSE]: Phase B / #047 — compute the multiclass spellcaster level
//   used in the activity-log line in useClassEffects. PHB p.165 RAW:
//
//     full caster level  = Bard / Cleric / Druid / Sorcerer / Wizard
//                          full classLevel
//     half caster level  = Paladin / Ranger floor(classLevel / 2)
//                          Artificer: ceil(classLevel / 2) — TCoE p.9
//     third caster level = Eldritch Knight / Arcane Trickster
//                          floor(classLevel / 3)
//     Warlock pact magic = does NOT contribute
//
// [WHY]: Pre-fix `useClassEffects.js` had a hardcoded
//   `subclass === 'eldritch knight' || subclass === 'arcane trickster'`
//   fallback to detect third-caster subclasses. That blocked any
//   custom CW subclass authoring `casterType: 'third'`. getCasterType
//   in contentResolver already handles the subclass lookup
//   case-insensitively; this helper just needs to call it.
//
// [INTEGRATION]: useClassEffects imports computeSpellcasterLevel and
//   uses it in the log line. Pure helper, no side effects.

import { getCasterType, getClassData } from './contentResolver';

/**
 * @param {Array<{className?: string, level?: number, subclass?: string}>|null|undefined} classes
 * @returns {number} Combined spellcaster level for multiclass spell slot calc.
 */
export function computeSpellcasterLevel(classes) {
  if (!Array.isArray(classes)) return 0;
  let total = 0;
  for (const cls of classes) {
    if (!cls || !cls.className) continue;
    const classLevel = Number(cls.level) || 0;
    if (classLevel <= 0) continue;

    // Try class-level caster type first; fall through to subclass.
    let casterType = getCasterType(cls.className);
    if (casterType !== 'full' && casterType !== 'half' && casterType !== 'third') {
      // Class wasn't a full/half/third caster on its own. Check if the
      // subclass turns it into one (Eldritch Knight, Arcane Trickster,
      // and any custom CW subclass declaring casterType: 'third').
      if (cls.subclass) {
        casterType = getCasterType(cls.subclass);
      }
    }
    // [SPR-007 — MAY 2026]: Artificer fallback. If Tasha's source isn't
    //   loaded (test env, custom-only-mode), getCasterType returns null
    //   for 'Artificer'. But Artificer is RAW half-caster per TCoE p.9
    //   regardless of source-load state.
    if ((!casterType || casterType === 'none') && cls.className === 'Artificer') {
      casterType = 'half';
    }

    if (casterType === 'full') total += classLevel;
    else if (casterType === 'half') {
      // [SPR-007 — MAY 2026]: Artificer (TCoE p.9) is the only half-caster
      //   that rounds UP for multiclass spellcaster level. All other
      //   half-casters (Paladin, Ranger) round DOWN.
      //
      //   Detection priority:
      //     1. Explicit `classData.spellcasting.halfCasterRoundUp: true` flag
      //        (future-proofs CW custom half-casters that author this).
      //     2. SRD-known className 'Artificer' fallback (works even when
      //        Tasha's source is disabled in test env — TCoE p.9 is the
      //        canonical rule, not source-gated).
      //     3. Otherwise: floor (Paladin / Ranger / generic half-caster).
      const classData = getClassData(cls.className) || (cls.subclass ? getClassData(cls.subclass) : null);
      const roundsUp = classData?.spellcasting?.halfCasterRoundUp === true
        || cls.className === 'Artificer';
      const roundFn = roundsUp ? Math.ceil : Math.floor;
      total += roundFn(classLevel / 2);
    }
    else if (casterType === 'third') total += Math.floor(classLevel / 3);
    // Warlock pact magic, non-casters → contribute 0.
  }
  return total;
}
